// ── the company's worlds ────────────────────────────────────────────────────
// Client-safe. One place that says what GROUND and what NETTI each company
// space wears, so a surface never invents a mood of its own.
//
// The company used to be one world: pale lavender, red netti, everywhere. That
// made blog · updates · compare · inside read as one undifferentiated stream —
// you could not tell from the corner of your eye which room you were in. Each
// space now has its own pair, and every pair is built the same way, so they
// read as a family rather than as four unrelated pages:
//
//   THE RELATION: the ground's hue sits 59° BELOW the organism's.
//
// That number is not chosen here — it is measured off the established company
// spread (lavender 288 ground under a 347 red netti), which is the one pairing
// John has already approved. Keeping the interval and moving the pair around
// the wheel is what makes a new room feel like the same product. The ground is
// always pale (L 88%) so the ink ladder — which is the ORGANISM's hue at 19%
// lightness — keeps the same contrast it has today, whatever room you are in.

import type { ColorState } from "../organism/color";

/** the hue interval between a room's ground and its organism (see above). */
export const GROUND_LEAD = 59;

const wrap = (h: number) => ((h % 360) + 360) % 360;

/** the pale ground that belongs under an organism of this hue. */
export function groundFor(organismHue: number, sat = 60, light = 88): string {
  return `hsl(${Math.round(wrap(organismHue - GROUND_LEAD))} ${sat}% ${light}%)`;
}

/**
 * A three-hue organism built from one identity hue. The secondary trails the
 * primary and the tinge leads it, which is the shape of the company's own red
 * netti (347 primary · 318 secondary · 10 tinge) generalised: −29 and +23.
 */
export function organismFor(
  hue: number,
  sat = 0.8,
  { light = 0.52, contrast = 0.4 }: { light?: number; contrast?: number } = {},
): ColorState {
  const s = Math.max(0.42, Math.min(0.9, sat));
  return {
    primary: { hue: wrap(hue), sat: s, light },
    secondary: { hue: wrap(hue - 29), sat: Math.max(0.38, s - 0.08), light: light - 0.06 },
    tinge: { hue: wrap(hue + 23), sat: Math.max(0.4, s - 0.06), light: light + 0.08 },
    contrast,
  };
}

export interface CoTheme {
  /** the page's `--bg` */
  ground: string;
  /** the organism living on the right */
  color: ColorState;
  /** how grown the creature is on an index (a reader ages with the scroll) */
  age: number;
}

/** the saturation and lightness every pale room ground is painted at */
export const GROUND_SAT = 60;
export const GROUND_LIGHT = 88;

const room = (hue: number, sat: number, age = 0.86): CoTheme => ({
  ground: groundFor(hue, GROUND_SAT, GROUND_LIGHT),
  color: organismFor(hue, sat),
  age,
});

/**
 * The company's rooms, as the three numbers each one IS — an identity hue, how
 * vivid its creature runs, and how grown it stands. `blog` keeps the
 * established lavender + red exactly (it is the surface people have already
 * seen) and every other room is the same construction moved around the wheel.
 *
 * The numbers live here rather than inside the built themes because a room's
 * hue is now read by two faces, not one: the page wears it live, and the
 * page's PICTURE is painted from it (lib/brand/portal). A theme object can't
 * answer "what hue is this room?" without being taken apart again.
 */
export const CO_ROOMS = {
  blog: { hue: 347, sat: 0.8, age: 0.86 },
  updates: { hue: 255, sat: 0.72, age: 0.86 },
  compare: { hue: 211, sat: 0.74, age: 0.86 },
  inside: { hue: 101, sat: 0.6, age: 0.86 },
  // a reading of the whole company — a deep amber, the colour of a ledger lamp
  reports: { hue: 38, sat: 0.7, age: 0.86 },
} as const;

export type CoRoom = keyof typeof CO_ROOMS;

/** the rooms, built. One per entry above — never hand-written beside it. */
export const CO_THEMES = Object.fromEntries(
  Object.entries(CO_ROOMS).map(([key, r]) => [key, room(r.hue, r.sat, r.age)]),
) as Record<CoRoom, CoTheme>;

/**
 * THE NIGHT ROOMS. Every stream is a pale ground with the creature seated to one
 * side; the two pages about US are dark, and netti IS the room. They pair on
 * purpose — about and team are the same kind of page — and they are told apart
 * by hue rather than by construction. The ink ladder is inverted locally for
 * both (`.co-night` in globals.css), never on :root: a page should be able to be
 * dark without the rest of the app finding out.
 */
export const ABOUT_THEME: CoTheme = {
  ground: "hsl(222 58% 17%)",
  color: organismFor(211, 0.82, { light: 0.56, contrast: 0.52 }),
  age: 0.92,
};

/** the team's own colour — the hue the team board itself is set in. */
export const TEAM_THEME: CoTheme = {
  ground: "hsl(346 40% 15%)",
  color: organismFor(6, 0.72, { light: 0.54, contrast: 0.5 }),
  age: 0.7,
};

/** the room a company post belongs to. */
export function roomForKind(kind: string, category?: string | null): CoRoom {
  if (category === "compare") return "compare";
  if (kind === "update") return "updates";
  if (kind === "inside") return "inside";
  if (kind === "report") return "reports";
  return "blog";
}

// ── a comparison's own world ────────────────────────────────────────────────
// Each comparison page takes the OTHER product's identity colour: their hue
// becomes the organism's, and the ground follows it at the house interval. So
// standing on the mem0 page you are in mem0's purple; on plur's, its cyan. The
// tile itself never changes — only the room around it — which is what makes the
// page feel written about them rather than stamped out of a template.
//
// The hues are MEASURED off each icon (the most-present saturated hue, weighted
// by saturation so a small vivid mark beats a big pale wash). Four marks are
// pure black-and-white and have no hue to measure; those carry a hand-picked
// one, spread apart so the monochrome set does not read as four copies.

export interface Tone {
  hue: number;
  sat: number;
  /** true when the hue was chosen rather than measured (a monochrome mark) */
  chosen?: boolean;
}

export function compareTheme(tone: Tone): CoTheme {
  return {
    // a measured hue can be extreme (mem0's lavender is 0.97 sat); the room has
    // to hold a page of prose, so the organism is allowed to be vivid and the
    // ground is held to one saturation for every comparison.
    ground: groundFor(tone.hue, 58, 89),
    color: organismFor(tone.hue, Math.min(0.82, Math.max(0.55, tone.sat))),
    age: 0.38,
  };
}
